import React from 'react'
import { FormikContext } from 'formik'
import type { FormikContextType } from 'formik'
import type { PresentationOptionV2 } from 'paintscout'

import { useClientOptions } from '@paintscout/ui-business'
import { getCartLinkButtonLabel } from '@paintscout/util/builder'

import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { hasPicturesBlock } from '../../util/hasBlockType'
import { usePresentationNav } from '../../PresentationNav'
import { useQuote } from '../../../context'

import QuoteComponent from '../../../Quote/Quote'
import { PackageGateOrEstimate } from '../../../Quote/views/QuoteView/TieredPackages/TieredPackageGateContext'
import QuotePlaceholder from './QuotePlaceholder'
import AnimationWrapper from '../../util/AnimationWrapper'

import ButtonBlock from '../Button/ButtonBlock'

function QuoteBlock(props: PresentationBlockProps) {
  const { isQuoteEmpty, presentation: navPresentation } = usePresentationNav()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  // Which presentation is on screen, so the document knows whether this one renders the quote's
  // photos itself (#22698). The canvas Formik values *are* that presentation, so this stays right
  // while the builder edits it; the nav context carries it on surfaces that mount blocks without a
  // Formik. `useContext` rather than `useFormikContext`, which throws instead of returning undefined
  // when no Formik is present. With neither source, leave the prop unset and let the document keep
  // its `!inPresentation` default — hiding Media is the old behaviour, showing it twice is not.
  // Read before the early return below to keep the hook order stable.
  const formik = React.useContext(FormikContext) as FormikContextType<PresentationOptionV2> | undefined
  const presentation = formik?.values ?? navPresentation
  const showMediaSection = presentation ? !hasPicturesBlock(presentation) : undefined
  if (isQuoteEmpty) return null
  const { WrapperComponent, isSettings, block, useCart, objectLabels } = props

  const animation = block.fields?.animation

  if (useCart) {
    const label = getCartLinkButtonLabel({ options, quote, label: block.fields?.label })
    return <ButtonBlock cartLink={true} {...props} block={{ ...block, fields: { ...block.fields, label } }} />
  } else
    return (
      <>
        <WrapperComponent {...props}>
          <AnimationWrapper animation={animation} index={0} direction={'top'}>
            <div>
              {isSettings ? (
                <QuotePlaceholder objectLabels={objectLabels} />
              ) : (
                <PackageGateOrEstimate>
                  <QuoteComponent inPresentation={true} showMediaSection={showMediaSection} />
                </PackageGateOrEstimate>
              )}
            </div>
          </AnimationWrapper>
        </WrapperComponent>
      </>
    )
}

export default React.memo(QuoteBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
